import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Paper } from '@mui/material';
import { People, Assessment } from '@mui/icons-material'; // Material-UI icons
import { X } from 'lucide-react'; // Lucide icon
import { useAdmin } from '../contexts/AdminContext';
import { adminApiService } from '../services/adminApiService';

const AdminToggle: React.FC = () => {
  const { isAdminMode, toggleAdminMode } = useAdmin();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

// Check JWT authentication status
useEffect(() => {
  const checkAuthStatus = () => {
    // Check if token exists in localStorage immediately (synchronous)
    const token = localStorage.getItem('admin_api_token');
    setIsAuthenticated(!!token);
  };

  // Check immediately on mount
  checkAuthStatus();

  // Also validate token properly (but don't wait for it to show/hide logout button)
  const validateToken = async () => {
    const isValid = await adminApiService.validateToken();
    if (!isValid && isAuthenticated) {
      // Only update if token is actually invalid
      setIsAuthenticated(false);
      if (isAdminMode) {
        toggleAdminMode();
      }
    }
  };

  validateToken();

  // Check auth status periodically (every 30 seconds) - but just for validation
  const interval = setInterval(validateToken, 30000);

  return () => clearInterval(interval);
}, [isAuthenticated, isAdminMode, toggleAdminMode]);

 // Listen for storage changes (when user logs out)
useEffect(() => {
  const handleStorageChange = async () => {
    const isValid = await adminApiService.validateToken();
    setIsAuthenticated(isValid);
    
    // If user logged out from another page, hide the admin panel
    if (!isValid && isAuthenticated && isAdminMode) {
      toggleAdminMode(); // Hide the panel
    }
  };

  const handleAdminCancel = () => {
    // Close admin panel when user cancels login
    if (isAdminMode) {
      toggleAdminMode();
    }
  };


  window.addEventListener('storage', handleStorageChange);
  window.addEventListener('admin-cancel', handleAdminCancel);
  
  // Remove the admin-logout event listener since we're handling it directly
  
  return () => {
    window.removeEventListener('storage', handleStorageChange);
    window.removeEventListener('admin-cancel', handleAdminCancel);
  };
}, [isAuthenticated, isAdminMode, toggleAdminMode]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Toggle admin panel with Ctrl + Shift + A
      if (event.ctrlKey && event.shiftKey && event.key === 'A') {
        event.preventDefault();
        toggleAdminMode();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [toggleAdminMode]);

  const handleAdminNavigation = async (path: string) => {
    // Check if authenticated first
    const isValid = await adminApiService.validateToken();
    
    if (isValid) {
      // Already authenticated, navigate directly
      navigate(path);
    } else {
      // Not authenticated, navigate to trigger password prompt
      navigate(path);
    }
  };

  // Only show panel if admin mode is toggled on
  if (!isAdminMode) {
    return null;
  }

  return (
    <Box className="fixed top-4 left-4 z-40">
      <Paper 
        elevation={3}
        sx={{ 
          p: 2, 
          backgroundColor: 'rgba(244, 67, 54, 0.9)',
          color: 'white',
          borderRadius: 2,
          minWidth: 150
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <div className="bg-red-600 text-white px-2 py-1 rounded text-xs font-mono">
            ADMIN MODE
          </div>
          <Button
            size="small"
            onClick={toggleAdminMode}
            sx={{ 
              minWidth: 'auto',
              p: 0.5,
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <X size={16} />
          </Button>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<Assessment />}
            onClick={() => handleAdminNavigation('/admin/leads')}
            sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' },
              textTransform: 'none',
              fontSize: '0.75rem'
            }}
          >
            Leads
          </Button>
          
          <Button
            variant="contained"
            size="small"
            startIcon={<People />}
            onClick={() => handleAdminNavigation('/admin/users')}
            sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' },
              textTransform: 'none',
              fontSize: '0.75rem'
            }}
          >
            Users
          </Button>


          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              // Clear any existing authentication
              adminApiService.logout();
              setIsAuthenticated(false);
              
              // If currently on an admin page, navigate away
              const currentPath = window.location.pathname;
              if (currentPath.startsWith('/admin/')) {
                navigate('/');
              }
              
              // Hide the admin panel
              toggleAdminMode();
            }}
            sx={{ 
              borderColor: 'rgba(255, 255, 255, 0.5)',
              color: 'white',
              '&:hover': { 
                borderColor: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)' 
              },
              textTransform: 'none',
              fontSize: '0.75rem',
              mt: 1
            }}
          >
            Logout
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminToggle;